// Typed API client. All calls are same-origin relative to /api.
// Ported from the global `API` variable pattern in ui.ts.

import type {
  AddressesResponse,
  AdminDeleteResponse,
  AdminMessagesResponse,
  AdminStats,
  AdminUsersResponse,
  ChallengeResponse,
  Label,
  LabelsResponse,
  MessageDetailResponse,
  MessagesResponse,
  NostrEvent,
  Proof,
  ProofsResponse,
  QuoteResponse,
  QuoteStatusResponse,
  SendResponse,
  SentResponse,
  VerifyResponse,
  MeResponse,
} from './types';
import { getCorrelationId } from './client-log';

const API_BASE = '/api';

// ─── Internal helpers ───

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('x-correlation-id', getCorrelationId());
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) msg = body.error;
    } catch { /* response had no JSON body */ }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

// ─── Auth ───

export const auth = {
  challenge: (): Promise<ChallengeResponse> =>
    fetchJson(`${API_BASE}/auth/challenge`, { method: 'POST' }),

  verify: (event: NostrEvent): Promise<VerifyResponse> =>
    fetchJson(`${API_BASE}/auth/verify`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ event }),
    }),

  logout: (): Promise<void> =>
    fetch(`${API_BASE}/auth/logout`, { method: 'POST' }).then(() => undefined),
};

export const users = {
  me: (): Promise<MeResponse> => fetchJson(`${API_BASE}/auth/me`),

  update: (patch: { nip17Notify?: boolean }): Promise<MeResponse> =>
    fetchJson(`${API_BASE}/users/me`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    }),
};

// ─── Messages ───

export const messages = {
  list: (): Promise<MessagesResponse> => fetchJson(`${API_BASE}/messages`),

  get: (id: string): Promise<MessageDetailResponse> =>
    fetchJson(`${API_BASE}/messages/${id}`),

  getRaw: async (id: string): Promise<Uint8Array> => {
    const res = await fetch(`${API_BASE}/messages/${id}/raw`);
    if (!res.ok) throw new Error('Failed to fetch encrypted content');
    return new Uint8Array(await res.arrayBuffer());
  },

  getAttachment: async (msgId: string, attId: string): Promise<Uint8Array> => {
    const res = await fetch(`${API_BASE}/messages/${msgId}/attachments/${attId}`);
    if (!res.ok) throw new Error('Fetch failed');
    return new Uint8Array(await res.arrayBuffer());
  },

  delete: (id: string): Promise<void> =>
    fetch(`${API_BASE}/messages/${id}`, { method: 'DELETE' }).then(() => undefined),

  addLabel: (messageId: string, labelId: number): Promise<void> =>
    fetchJson(`${API_BASE}/messages/${messageId}/labels`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ labelId }),
    }).then(() => undefined),

  removeLabel: (messageId: string, labelId: number): Promise<void> =>
    fetch(`${API_BASE}/messages/${messageId}/labels/${labelId}`, { method: 'DELETE' }).then(() => undefined),
};

// ─── Sent ───

export const sent = {
  list: (): Promise<SentResponse> => fetchJson(`${API_BASE}/sent`),
};

export const labels = {
  list: (): Promise<LabelsResponse> => fetchJson(`${API_BASE}/labels`),

  create: (name: string, color?: string): Promise<Label> =>
    fetchJson(`${API_BASE}/labels`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, color }),
    }),

  delete: (id: number): Promise<void> =>
    fetch(`${API_BASE}/labels/${id}`, { method: 'DELETE' }).then(() => undefined),
};

// ─── Addresses ───

export const addresses = {
  list: (): Promise<AddressesResponse> => fetchJson(`${API_BASE}/addresses`),

  create: (localPart: string, cashuToken: string): Promise<unknown> =>
    fetchJson(`${API_BASE}/addresses`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ localPart, cashuToken }),
    }),

  delete: (id: number): Promise<unknown> =>
    fetchJson(`${API_BASE}/addresses/${id}`, { method: 'DELETE' }),
};

// ─── Proofs ───

export const proofs = {
  list: (): Promise<ProofsResponse> => fetchJson(`${API_BASE}/proofs`),

  // Exposed as array for convenience
  listArray: async (): Promise<Proof[]> => {
    const data = await proofs.list();
    return data.proofs || [];
  },
};

// ─── Send (compose + payment) ───

export const send = {
  quote: (mintUrl?: string): Promise<QuoteResponse> =>
    fetchJson(`${API_BASE}/send/quote`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ mintUrl }),
    }),

  quoteStatus: (quoteId: string): Promise<QuoteStatusResponse> =>
    fetchJson(`${API_BASE}/send/quote/${quoteId}/status`),

  send: (
    to: string,
    subject: string,
    text: string,
    payment: { cashuToken?: string; paidQuoteId?: string },
  ): Promise<SendResponse> =>
    fetchJson(`${API_BASE}/send`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ to, subject, text, ...payment }),
    }),
};

// ─── Admin ───

export const admin = {
  stats: (secret: string): Promise<AdminStats> =>
    fetchJson(`${API_BASE}/admin/stats`, {
      headers: { 'x-admin-secret': secret },
    }),

  messages: (
    secret: string,
    limit = 20,
    offset = 0,
  ): Promise<AdminMessagesResponse> =>
    fetchJson(
      `${API_BASE}/admin/messages?limit=${limit}&offset=${offset}`,
      { headers: adminHeaders(secret) },
    ),

  users: (
    secret: string,
    limit = 20,
    offset = 0,
  ): Promise<AdminUsersResponse> =>
    fetchJson(
      `${API_BASE}/admin/users?limit=${limit}&offset=${offset}`,
      { headers: adminHeaders(secret) },
    ),

  deleteMessage: (secret: string, id: string): Promise<AdminDeleteResponse> =>
    fetchJson(`${API_BASE}/admin/messages/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(secret),
    }),

  quarantine: (secret: string): Promise<any> =>
    fetchJson(`${API_BASE}/admin/quarantine`, { headers: { 'x-admin-secret': secret } }),

  auditLog: (secret: string, limit = 50): Promise<any> =>
    fetchJson(`${API_BASE}/admin/audit-log?limit=${limit}`, { headers: { 'x-admin-secret': secret } }),

  deleteQuarantine: (secret: string, id: string): Promise<any> =>
    fetchJson(`${API_BASE}/admin/quarantine/${id}`, { method: 'DELETE', headers: adminHeaders(secret) }),

  releaseQuarantine: (secret: string, id: string): Promise<any> =>
    fetchJson(`${API_BASE}/admin/quarantine/${id}/release`, { method: 'POST', headers: adminHeaders(secret) }),
};

function adminHeaders(secret: string): Record<string, string> {
  return { 'x-admin-secret': secret, 'content-type': 'application/json' };
}
